import { useCallback } from 'react';
import type { GridApi } from 'ag-grid-community';
import type { IMATEntry } from '../../../types/mat-types';
import { buildOrdering, readUrlParam, buildColumnDefsFromRow } from '../MATGrid.utils';
import { MAT_STATUS_OPTIONS } from '../../../types/mat-types';
import type {
  UseMatDataSourceArgs,
  GetRowsParams,
  TransformedMATRow,
} from './useMatDataSource.types';
import { calculatePaginationSlice } from './useMatDataSource.utils';

const DEFAULT_MAT_ORDERING = '-created_at';

/** Transform UI filter keys to API keys: std-X → StdX, nrm-X → NrmX, c-X → C_X. */
const FILTER_PREFIX_MAP: Record<string, string> = {
  'std-': 'Std',
  'nrm-': 'Nrm',
  'c-': 'C_',
};

function toApiFilterKey(key: string): string {
  for (const [prefix, replacement] of Object.entries(FILTER_PREFIX_MAP)) {
    if (key.startsWith(prefix)) return `${replacement}${key.slice(prefix.length)}`;
  }
  return key;
}

/**
 * Custom hook for managing AG Grid infinite row model data source for MAT entries
 * Combines server-paginated MAT data with client-side filtered discovered data
 */
export default function useMatDataSource({
  glyntPromise,
  orgId,
  onTotalCountChange,
  setLoading,
  setError,
  setColumnDefs,
  columnDefsInitialized,
  dataSourceInitialized,
  serverFilterFields,
  includeMatDataRef,
  includeDiscoveredRef,
  transformedDiscoveredRowsRef,
  currentSearchRef,
  dataRequestVersionRef,
}: UseMatDataSourceArgs) {
  return useCallback(
    (api: GridApi | null) => {
      if (!orgId || !api) return;
      if (dataSourceInitialized.current) return;

      const dataSource = {
        getRows: async (params: GetRowsParams) => {
          const requestVersion = dataRequestVersionRef.current;
          setError(null);
          setLoading(true);

          try {
            const g = await glyntPromise;
            const { startRow, endRow, sortModel } = params;
            const limit = endRow - startRow;
            const offset = startRow;
            const ordering = buildOrdering(sortModel) || DEFAULT_MAT_ORDERING;

            // Parse URL filters once per request
            // Use the same search params that were used to filter discovered rows
            const urlSp = new URLSearchParams(currentSearchRef.current);
            const filters: Record<string, string> = {};
            serverFilterFields.forEach((key) => {
              const value = readUrlParam(key, urlSp, MAT_STATUS_OPTIONS as readonly string[]);
              if (value != null && value !== '') {
                filters[key] = value;
              }
            });

            // Transform filter keys for the API (std-X → StdX, nrm-X → NrmX)
            const apiFilters: Record<string, string> = {};
            for (const [k, v] of Object.entries(filters)) {
              apiFilters[toApiFilterKey(k)] = v;
            }

            // Get pre-transformed discovered rows
            const discoveredRows = includeDiscoveredRef.current
              ? transformedDiscoveredRowsRef.current
              : [];

            // Calculate pagination slice
            const { discoveredSlice, matOffset, matLimit } = calculatePaginationSlice(
              offset,
              limit,
              discoveredRows,
              includeDiscoveredRef.current
            );

            // Fetch MAT data if needed
            let matRows: IMATEntry[] = [];
            let matCount = 0;

            if (includeMatDataRef.current) {
              // Always fetch to get the total count, even if we don't need rows on this page
              const res = await g.mat.list(orgId, {
                advanced: true,
                limit: matLimit,
                offset: matOffset,
                ordering,
                ...apiFilters,
              });
              matRows = res?.results || [];
              matCount = res?.count || 0;
            }

            // A filter change purges the infinite cache and starts a replacement
            // request. Do not let a slower response from the previous filter state
            // overwrite the new rows or totals.
            if (requestVersion !== dataRequestVersionRef.current) return;

            // Combine discovered and MAT data
            const allRows: TransformedMATRow[] = [...discoveredSlice, ...matRows];
            const totalCount = discoveredRows.length + matCount;

            // Update total count callback with separate counts
            if (typeof onTotalCountChange === 'function') {
              onTotalCountChange(matCount, discoveredRows.length);
            }

            // Initialize column definitions from first row
            if (!columnDefsInitialized.current && allRows.length > 0) {
              const firstRow = allRows[0];
              const columnDefs = buildColumnDefsFromRow(firstRow, serverFilterFields);
              setColumnDefs(columnDefs);
              columnDefsInitialized.current = true;
            }

            params.successCallback(allRows, totalCount);
          } catch (error: unknown) {
            if (requestVersion !== dataRequestVersionRef.current) return;
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to load MAT entries';
            setError(errorMessage);
            params.failCallback();
          } finally {
            if (requestVersion === dataRequestVersionRef.current) {
              setLoading(false);
            }
          }
        },
      };

      // Set datasource
      if ('setDatasource' in api && typeof (api as any).setDatasource === 'function') {
        (api as any).setDatasource(dataSource);
      } else if (api.setGridOption) {
        api.setGridOption('datasource', dataSource);
      }

      dataSourceInitialized.current = true;
    },
    [
      orgId,
      glyntPromise,
      onTotalCountChange,
      setLoading,
      setError,
      setColumnDefs,
      columnDefsInitialized,
      dataSourceInitialized,
      serverFilterFields,
      includeMatDataRef,
      includeDiscoveredRef,
      transformedDiscoveredRowsRef,
      currentSearchRef,
      dataRequestVersionRef,
    ]
  );
}
